import { capabilityFields, receiptFields } from "../content";

const stages = [
  "Identity",
  "Intent",
  "Authority",
  "Capability",
  "Policy — ALLOW",
];

export function HeroFlow() {
  return (
    <div
      className="hero-flow diagram-shell"
      aria-label="Agent request checked by the ORIQ Trust Gateway before execution"
    >
      <div className="flow-row flow-row-main">
        <FlowNode label="AI AGENT" detail="ACTION REQUEST" />
        <div className="flow-arrow" aria-hidden="true" />
        <div className="gateway-node">
          <span className="node-kicker">CONTROL PLANE</span>
          <strong>ORIQ TRUST GATEWAY</strong>
          <div className="gateway-checks">
            {stages.map((stage, index) => (
              <span
                className="check-row"
                style={{ "--delay": `${index * 0.45}s` } as React.CSSProperties}
                key={stage}
              >
                <i>✓</i>
                {stage}
              </span>
            ))}
          </div>
        </div>
        <div className="flow-arrow" aria-hidden="true" />
        <FlowNode label="EXTERNAL SYSTEM" detail="EXECUTED" />
      </div>
      <div className="flow-result">
        <span>VERIFIED</span>
        <span>↓</span>
        <strong>ACTION RECEIPT ✓</strong>
      </div>
    </div>
  );
}

function FlowNode({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="flow-node">
      <span className="node-kicker">{label}</span>
      <strong>{detail}</strong>
    </div>
  );
}

export function TrustLayerDiagram() {
  return (
    <div className="trust-layer diagram-shell">
      <div className="vertical-trace">
        {["PRINCIPAL", "INTENT", "AUTHORITY", "AGENT"].map((item) => (
          <div className="trace-node" key={item}>
            <span className="trace-dot" />
            {item}
          </div>
        ))}
        <div className="gateway-stack">
          <span className="node-kicker">CONTROL PLANE</span>
          <strong>ORIQ TRUST GATEWAY</strong>
          <div className="stack-grid">
            {stages.map((stage) => (
              <span key={stage}>
                <i>✓</i>
                {stage.replace(" — ALLOW", "")}
              </span>
            ))}
          </div>
          <em>AUTHORIZED</em>
        </div>
        {["EXTERNAL SYSTEM", "EXECUTION", "VERIFICATION"].map((item) => (
          <div className="trace-node" key={item}>
            <span className="trace-dot" />
            {item}
          </div>
        ))}
        <div className="receipt-mini">
          <span>ACTION RECEIPT</span>
          <strong>VERIFIED</strong>
          <code>receipt_hash: 7f2a...c14e</code>
        </div>
      </div>
    </div>
  );
}

export function CapabilityCard() {
  return (
    <div className="capability-card protocol-object">
      <div className="object-head">
        <span>CAPABILITY</span>
        <b>ACTIVE</b>
      </div>
      <div className="field-list">
        {capabilityFields.map(([label, value]) => (
          <div className="field-row" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <div className="formula">Authority(child) ⊆ Authority(parent)</div>
    </div>
  );
}

export function PolicyDiagram() {
  const inputs = [
    "ACTION",
    "INTENT",
    "AUTHORITY",
    "CAPABILITY",
    "CONTEXT",
    "RISK",
    "POLICY",
  ];
  return (
    <div className="policy-diagram diagram-shell">
      <div className="policy-inputs">
        {inputs.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </div>
      <div className="policy-core">
        <span className="node-kicker">DECISION FUNCTION</span>
        <strong>ORIQ POLICY ENGINE</strong>
        <code>
          F(action, intent, authority, capability, context, risk, policy)
        </code>
      </div>
      <div className="decision-list">
        <span className="decision-deny">DENY</span>
        <span className="decision-review">REVIEW</span>
        <span className="decision-pause">PAUSE</span>
        <span className="decision-allow">ALLOW ✓</span>
      </div>
    </div>
  );
}

export function ReceiptCard() {
  return (
    <div className="receipt-card protocol-object">
      <div className="receipt-head">
        <span>ACTION RECEIPT</span>
        <b>VERIFIED</b>
      </div>
      <div className="receipt-fields">
        {receiptFields.map(([label, value]) => (
          <div className="receipt-row" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <code>receipt_hash: 7f2a2d9b...c14e</code>
    </div>
  );
}

export function InfrastructureDiagram() {
  return (
    <div className="infrastructure diagram-shell">
      <div className="infra-origin">AGENT RUNTIMES</div>
      <div className="infra-core">ORIQ</div>
      <div className="infra-grid">
        {[
          "IAM",
          "OAuth",
          "MCP",
          "API",
          "Cloud",
          "KMS",
          "Payment Rails",
          "Blockchain",
        ].map((item) => (
          <span key={item}>{item}</span>
        ))}
      </div>
    </div>
  );
}

export function NetworkGraph() {
  return (
    <div className="network-graph diagram-shell">
      <div className="network-orbit" aria-hidden="true" />
      <div className="network-points">
        {["Principal", "Agent", "Provider", "Verifier", "Validator"].map(
          (item, index) => (
            <span className={`network-point point-${index + 1}`} key={item}>
              <i />
              {item}
            </span>
          ),
        )}
      </div>
      <div className="network-caption">
        ACTION RECEIPT <span>→</span> VERIFIER <span>→</span> VALIDATOR{" "}
        <span>→</span> SHARED TRUST
      </div>
    </div>
  );
}
